import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { captureException } from '@sentry/browser';

import { useGetOnDemandCirclesQuery } from '../documents/getOnDemandCircles.generated';
import { useLanguageCode } from '../hooks/useLanguageCode';

import { CircleOnDemandCard, CircleOnDemandCardSkeleton } from './CircleOnDemandCard';
import { CircleOnDemandRow } from './CircleOnDemandRow';

export const CircleLatestOnDemandRowSkeleton = ({ header }: { header: string }) => {
    return (
        <CircleOnDemandRow header={header}>
            <CircleOnDemandCardSkeleton />
            <CircleOnDemandCardSkeleton />
            <CircleOnDemandCardSkeleton />
        </CircleOnDemandRow>
    );
};

export const CircleLatestOnDemandRow = () => {
    const languageCode = useLanguageCode();
    const { t } = useTranslation('circles');
    const first = 10;
    const { data, loading, error } = useGetOnDemandCirclesQuery({
        variables: {
            first: 100,
        },
    });

    useEffect(() => {
        if (error) captureException(error);
    }, [error]);

    if (loading && !data?.circleOnDemandConnection) {
        return <CircleLatestOnDemandRowSkeleton header={t('sections.on_demand.header')} />;
    }

    if (data?.circleOnDemandConnection.__typename !== 'CircleOnDemandConnection') {
        return null;
    }

    const edges = data.circleOnDemandConnection.edges
        .filter(edge => edge?.node.languageCode === languageCode)
        .slice(0, first);

    return (
        <CircleOnDemandRow
            to="/circles/on-demand"
            header={t('sections.on_demand.header')}
            cta={t('sections.series.view_all')}
        >
            {edges.map((edge, i) => {
                const ondemand = edge?.node;
                if (!ondemand) return null;

                return (
                    <CircleOnDemandCard
                        seconds={ondemand.video.durationInSeconds}
                        host={ondemand.facilitators[0]?.name}
                        languageCode={ondemand.languageCode}
                        thumb={ondemand.video.thumbnail}
                        key={`${ondemand.id}-${i}`}
                        name={ondemand.name}
                        slug={ondemand.slug}
                    />
                );
            })}
        </CircleOnDemandRow>
    );
};
